import React, { Fragment, Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PersonBadge,
  BoxSeam,
  BarChartLine,
  People,
  Trash3,
  ExclamationTriangle,
  Lightning,
  PersonPlus,
  Search,
  XLg,
  Wallet2,
  Receipt,
  Check2,
  Megaphone,
  Calendar3,
  PlusLg,
  ChevronRight,
  BoxArrowUpRight,
  Truck,
  Shop,
  Stars,
  CheckCircleFill,
  Gift,
  Tag,
  Phone,
  CalendarEvent,
  PiggyBank,
  PersonLinesFill,
  PersonHeart,
  CreditCard,
  Sliders,
} from 'react-bootstrap-icons';
import DiscountsTab from './DiscountsTab';
import BrandBuildingSection from './building/BrandBuildingSection';
import { useLocale } from '../i18n/LocaleContext';
import { useAuth } from '../auth/AuthContext';
import { tabAvailable } from '../config/embedded';
import {
  updateBrandProfile,
  updateBrandIban,
  toggleBrandVisibility,
  deleteBrand,
  fetchBrandProducts,
  fetchBrandDrops,
  createDrops,
  cancelDrop,
  syncProducts,
  fetchSyncProgress,
  fetchBrand,
  fetchBrandMembers,
  addBrandMember,
  removeBrandMember,
  updateBrandMember,
  searchUsers,
  fetchStatsOverview,
  fetchRevenue,
  fetchBrandOrders,
  fetchPayouts,
  requestPayout,
  permissionsForUser,
  BRAND_PERMISSIONS,
  fetchBrandCampaigns,
  fetchBrandCampaign,
  createBrandCampaign,
  selectCampaignApplicant,
  rejectCampaignApplicant,
  fetchLabelDisputes,
} from '../api/brands';
import ShippingTab from './ShippingTab';
import GamesTab from './GamesTab';
import ConfirmDialog from './ConfirmDialog';
import LabelingBadge from './LabelingBadge';
import LabelDispute from './LabelDispute';
import useLabelingPoll from './useLabelingPoll';
import ChallengeCalendarTab from './ChallengeCalendarTab';
import PrizePoolTab from './PrizePoolTab';
import { hasPendingLabeling, withLabeling, latestDisputesByProduct, LABELING_POLL_MS } from './productLabeling';
import { LogoField, CoverField, useImagePick } from './brand/BrandMedia';
import { SHOW_BILLING_PAGE, SHOW_FOLLOWERS_PAGE } from '../config/embedFlags';
import './BrandDashboard.scss';

// Money for display. Amounts can be derived (e.g. the gross reconstructed from
// the net), so they are rounded: whole forints for HUF, cents otherwise.
function formatMoney(n, currency) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const code = (currency || '').toUpperCase();
  const digits = code === 'HUF' ? 0 : 2;
  const num = Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: digits });
  return `${num} ${code}`.trim();
}

// The fit-check embed pages load on demand: the chart library they bring is
// only needed there.
const EmbedStatsTab = lazy(() => import('./embed/EmbedStatsTab'));
const EmbedVisitorsTab = lazy(() => import('./embed/EmbedVisitorsTab'));
const EmbedSettingsTab = lazy(() => import('./embed/EmbedSettingsTab'));
const FollowersTab = lazy(() => import('./brand/FollowersTab'));
// Stripe Elements come in with this page only.
const BillingTab = lazy(() => import('./billing/BillingTab'));

// What the signed-in user may do on this brand.
//
// Portal: from the signed-in user's own membership, which the session carries
// as the brand's only stores[].members entry (see permissionsForUser).
//
// Embedded: the session came from a Shopify session token, which Shopify only
// issues to staff of this shop. There is no Outfino user behind it, and the API
// acts as the brand's owner for it (the embedded session reports exactly that
// membership), so the merchant is treated as the owner. Which tabs actually
// exist there is a separate question — see tabAvailable / config/embedded.js.
function useBrandPermissions(brand) {
  const { user, embedded } = useAuth();
  return useMemo(() => {
    if (embedded) return new Set(BRAND_PERMISSIONS);
    if (!brand) return new Set();
    return permissionsForUser(brand, user?.id, user?.admin);
  }, [brand, user, embedded]);
}

// Every brand page with the permission it needs. `group` is the sidebar
// category it sits in, `parent` the page it is a sub-page of; BRAND_NAV is the
// sidebar order, where a `tab` entry is a page shown at the top level.
//
// dev: the 'mobile' group is shown (main hides it) — the brand building on the
// Profile, the community calendar and the Prize Pool live there.
export const BRAND_NAV = [
  { group: 'mobile', Icon: Phone, visible: true },
  { tab: 'campaigns', visible: false },
  // Flat top-level pages (no group): the fit-check embed pages, followers,
  // settings (with the brand profile on top), billing, members, the danger
  // zone.
  { tab: 'embedded-stats', visible: true },
  { tab: 'embedded-visitors', visible: true },
  { tab: 'followers', visible: true },
  { tab: 'embedded-settings', visible: true },
  { tab: 'billing', visible: true },
  { tab: 'members', visible: true },
  { tab: 'special', visible: true },
];

/// The page the bare dashboard path opens: Statisztikák when this user may
/// open it, otherwise the first page in the menu.
export const DEFAULT_TAB = 'embedded-stats';
export const LANDING_TAB = DEFAULT_TAB;

export function landingTab(tabs) {
  return tabs.some((x) => x.key === DEFAULT_TAB) ? DEFAULT_TAB : tabs[0]?.key ?? null;
}

const HIDDEN_NAV = new Set(BRAND_NAV.filter((entry) => !entry.visible).map((entry) => entry.group || entry.tab));

// Pages behind a release flag (config/embedFlags.js).
export function releasedTab(key, flags = { followers: SHOW_FOLLOWERS_PAGE, billing: SHOW_BILLING_PAGE }) {
  return flags[key] !== false;
}

const navVisible = ({ key, group }) => !HIDDEN_NAV.has(group || key) && releasedTab(key);

// The Tagok toggles: "Számlázás kezelése" only while Számlázás is shown.
const MEMBER_PERMISSIONS = BRAND_PERMISSIONS.filter((p) => p !== 'manageBilling' || SHOW_BILLING_PAGE);

const BRAND_TABS = [
  // The app's full brand profile (company, tagline, IBAN, visibility…), with
  // the hidden mobile pages. Its name, logo and cover part is the Márkaprofil
  // section of Beállítások.
  { key: 'profile', group: 'mobile', Icon: PersonBadge, perm: null },
  { key: 'products', group: 'mobile', Icon: BoxSeam, perm: 'viewProducts' },
  { key: 'drops', group: 'mobile', Icon: Lightning, perm: 'viewProducts' },
  { key: 'discounts', group: 'mobile', Icon: Tag, perm: 'viewProducts' },
  // Challenges / Játékok — galaxy gift drops + the prize wheel (GamesTab). A
  // marketing tool, so it needs the campaign permission.
  { key: 'challenges', group: 'mobile', Icon: Gift, perm: 'manageCampaigns' },
  { key: 'stats', group: 'mobile', Icon: BarChartLine, perm: 'viewAnalytics' },
  { key: 'orders', group: 'mobile', Icon: Receipt, perm: 'viewAnalytics' },
  // Eseménynaptár — the community challenge calendar, read-only.
  { key: 'calendar', group: 'mobile', Icon: CalendarEvent, perm: 'viewProducts' },
  // Prize Pool — the brand's rewards for the community challenges (editing
  // needs manageCampaigns, like the other marketing tools).
  { key: 'prizePool', group: 'mobile', Icon: PiggyBank, perm: 'viewProducts' },
  { key: 'shipping', group: 'mobile', Icon: Truck, perm: 'viewProducts' },
  { key: 'payouts', group: 'mobile', Icon: Wallet2, perm: 'viewAnalytics' },
  { key: 'campaigns', Icon: Megaphone, perm: 'manageCampaigns' },
  { key: 'embedded-stats', Icon: BarChartLine, perm: 'viewAnalytics' },
  { key: 'embedded-visitors', Icon: PersonLinesFill, perm: 'viewAnalytics' },
  { key: 'followers', Icon: PersonHeart, perm: 'viewAnalytics' },
  // The key, the button's design and the code to paste: brand settings.
  { key: 'embedded-settings', Icon: Sliders, perm: 'editBrand' },
  // Everyone on the brand sees the page; the owner (or the billing
  // permission) changes the card and the details — see api/billing.js.
  { key: 'billing', Icon: CreditCard, perm: null },
  { key: 'members', Icon: People, perm: 'manageMembers' },
  { key: 'special', Icon: ExclamationTriangle, perm: 'editBrand' },
];

// Pages that were removed: an old link lands on the page that replaced them
// (`#anchor` = the section on that page).
export const TAB_REDIRECTS = {
  profile: 'embedded-settings#brand-profile',
  'embedded-categories': 'embedded-stats',
  'embedded-buyers': 'embedded-visitors',
  'embedded-viewers': 'embedded-visitors',
};

// The community calendar and the Prize Pool are real pages (calendar,
// prizePool); no placeholder pages are left.
const COMING_SOON_TABS = new Set([
]);

// The pages this user can open on this brand. Gated by permission, mirroring
// the mobile app.
export function useBrandTabs(brand) {
  const perms = useBrandPermissions(brand);
  return useMemo(
    () => BRAND_TABS.filter((tab) => (!tab.perm || perms.has(tab.perm)) && tabAvailable(tab.key) && navVisible(tab)),
    [perms]
  );
}

// The body of the selected brand page. Which page is chosen in the dashboard
// sidebar (Dashboard.js); a tab this user can't open renders nothing.
export default function BrandDashboard({ brand, tab }) {
  const { t } = useLocale();
  const { user, refresh, embedded } = useAuth();
  const navigate = useNavigate();
  const perms = useBrandPermissions(brand);

  return (
    <div className="brand-dash">
      <div className="brand-dash__head">
        <div className="brand-dash__id">
          {brand.logo ? (
            <img className="brand-dash__logo" src={brand.logo} alt={brand.name} />
          ) : (
            <span className="brand-dash__logo brand-dash__logo--ph">
              {brand.name?.[0]?.toUpperCase() || 'B'}
            </span>
          )}
          <div>
            <h1 className="brand-dash__name">
              {brand.name}
              {user?.environment === 'dev' && brand.environment === 'dev' && (
                <span className="brand-dash__dev">{t('shell.envDev')}</span>
              )}
            </h1>
            {/* Prefer the brand description; fall back to the website/domain. */}
            {(brand.description || brand.website) && (
              <span className="brand-dash__site">
                {brand.description || brand.website}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="brand-dash__panel">
        {tab === 'profile' && (
          <>
            {/* "This is how your brand looks in Outfino" — the store on the
                Style Universe map, with the custom-building offer. */}
            <BrandBuildingSection brand={brand} canRequest={perms.has('editBrand')} />
            <ProfileTab brand={brand} canEdit={perms.has('editBrand')} onSaved={refresh} />
          </>
        )}
        {tab === 'followers' && tabAvailable(tab) && perms.has('viewAnalytics') && (
          <Suspense fallback={<div className="panel-card__muted">{t('shell.loading')}</div>}>
            <FollowersTab brand={brand} />
          </Suspense>
        )}
        {tab === 'billing' && tabAvailable(tab) && (
          <Suspense fallback={<div className="panel-card__muted">{t('shell.loading')}</div>}>
            <BillingTab brand={brand} />
          </Suspense>
        )}
        {tab === 'products' && perms.has('viewProducts') && (
          <ProductsTab
            brand={brand}
            canSync={perms.has('editProducts')}
            canRelabel={perms.has('editProducts')}
          />
        )}
        {tab === 'drops' && tabAvailable('drops') && perms.has('viewProducts') && (
          <DropsTab brand={brand} canEdit={!embedded && perms.has('editProducts')} />
        )}
        {tab === 'discounts' && tabAvailable('discounts') && perms.has('viewProducts') && (
          <DiscountsTab brand={brand} canEdit={perms.has('editProducts')} />
        )}
        {tab === 'campaigns' && tabAvailable('campaigns') && perms.has('manageCampaigns') && (
          <CampaignsTab brand={brand} canEdit={perms.has('manageCampaigns')} />
        )}
        {tab === 'challenges' && tabAvailable('challenges') && perms.has('manageCampaigns') && (
          <GamesTab brand={brand} canEdit={perms.has('manageCampaigns')} />
        )}
        {tab === 'stats' && perms.has('viewAnalytics') && <StatsTab brand={brand} />}
        {tab === 'orders' && perms.has('viewAnalytics') && <OrdersTab brand={brand} />}
        {tab === 'calendar' && tabAvailable('calendar') && perms.has('viewProducts') && (
          <ChallengeCalendarTab brand={brand} onOpenPrizePool={() => navigate(`${embedded ? '' : '/dashboard'}/prizePool`)} />
        )}
        {tab === 'prizePool' && tabAvailable('prizePool') && perms.has('viewProducts') && (
          <PrizePoolTab brand={brand} canEdit={perms.has('manageCampaigns')} />
        )}
        {tab === 'shipping' && tabAvailable('shipping') && perms.has('viewProducts') && (
          <ShippingTab brand={brand} />
        )}
        {tab === 'payouts' && perms.has('viewAnalytics') && <PayoutsTab brand={brand} />}
        {tab === 'members' && perms.has('manageMembers') && (
          <MembersTab
            brand={brand}
            currentUserId={user?.id}
            isAdmin={user?.admin}
          />
        )}
        {/* Fit-check embed (embed.outfino.io): Statisztikák, Látogatók,
            Beállítások. */}
        {tab === 'embedded-stats' && tabAvailable(tab) && perms.has('viewAnalytics') && (
          <Suspense fallback={<div className="panel-card__muted">{t('shell.loading')}</div>}>
            <EmbedStatsTab brand={brand} />
          </Suspense>
        )}
        {tab === 'embedded-visitors' && tabAvailable(tab) && perms.has('viewAnalytics') && (
          <Suspense fallback={<div className="panel-card__muted">{t('shell.loading')}</div>}>
            <EmbedVisitorsTab brand={brand} />
          </Suspense>
        )}
        {tab === 'embedded-settings' && tabAvailable(tab) && perms.has('editBrand') && (
          <Suspense fallback={<div className="panel-card__muted">{t('shell.loading')}</div>}>
            <EmbedSettingsTab brand={brand} canEditBrand={perms.has('editBrand')} onBrandSaved={refresh} />
          </Suspense>
        )}
        {COMING_SOON_TABS.has(tab) && tabAvailable(tab) && (
          <div className="brand-dash__soon">{t('shell.comingSoon')}</div>
        )}
        {tab === 'special' && tabAvailable('special') && perms.has('editBrand') && (
          <DangerZone brand={brand} onDeleted={refresh} />
        )}
      </div>
    </div>
  );
}

// ── Danger zone (remove brand) ──────────────────────────────────────────────

function DangerZone({ brand, onDeleted }) {
  const { t } = useLocale();
  const { embedded } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function handleDelete() {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('danger.confirm', { name: brand.name }))) return;
    setBusy(true);
    setError('');
    try {
      await deleteBrand(brand.id);
    } catch (err) {
      setError(err?.message ? `${t('danger.error')} (${err.message})` : t('danger.error'));
      setBusy(false);
      return;
    }
    if (!embedded) navigate('/dashboard', { replace: true });
    if (onDeleted) await onDeleted();
  }

  return (
    <div className="danger-zone">
      <div>
        <div className="danger-zone__title">{t('danger.title')}</div>
        <div className="danger-zone__body">{t('danger.deleteBody')}</div>
        {error && <div className="danger-zone__error">{error}</div>}
      </div>
      <button className="danger-zone__btn" onClick={handleDelete} disabled={busy}>
        <Trash3 size={15} style={{ marginRight: 6 }} />
        {busy ? t('danger.deleting') : t('danger.deleteBrand')}
      </button>
    </div>
  );
}

// ── Profile tab (all fields: name, company, tagline, description, phone, website, address, logo, cover, IBAN, visibility) ──

/// Max length of the one-line claim under the brand name — mirrors the API's
/// brands.tagline VARCHAR(140) (the server also trims / caps it).
export const BRAND_TAGLINE_MAX = 140;

function ProfileTab({ brand, canEdit, onSaved }) {
  const { t } = useLocale();
  const [name, setName] = useState(brand.name || '');
  const [company, setCompany] = useState(brand.companyName || '');
  const [tagline, setTagline] = useState(brand.tagline || '');
  const [description, setDescription] = useState(brand.description || '');
  const [phone, setPhone] = useState(brand.phone || '');
  const [website, setWebsite] = useState(brand.website || '');
  const [address, setAddress] = useState(brand.address || '');
  const [iban, setIban] = useState('');
  const [isActive, setIsActive] = useState(brand.isActive !== false);
  const logo = useImagePick(brand.logo);
  // Cover image: picked file is uploaded with the profile (multipart `cover`,
  // see updateBrandProfile) and stored as storage/brands/<id>/cover.jpg; the
  // session brand carries it as `coverImage` (GET /brands/:id: `cover_image`).
  const cover = useImagePick(brand.coverImage || brand.cover_image);
  const [state, setState] = useState('idle'); // idle | saving | saved | error
  // Drives the toast's exit animation: true = playing the fade-out before unmount.
  const [toastLeaving, setToastLeaving] = useState(false);

  // Dismiss the save toast gracefully: play the exit animation, then unmount.
  function dismissToast() {
    setToastLeaving(true);
    setTimeout(() => {
      setState('idle');
      setToastLeaving(false);
    }, 320); // must match the save-toast-out animation duration
  }

  // The session brand doesn't carry iban/is_active — fetch the full brand once.
  // The same read is the source of truth for the stored cover / tagline (an
  // older session payload may predate them).
  useEffect(() => {
    let alive = true;
    fetchBrand(brand.id)
      .then((full) => {
        if (!alive || !full) return;
        if (full.iban != null) setIban(full.iban);
        if (full.is_active != null) setIsActive(!!full.is_active);
        if (full.tagline != null) setTagline(full.tagline);
        if (full.cover_image) cover.setInitial(full.cover_image);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.id]);

  async function handleSave(e) {
    e.preventDefault();
    if (!canEdit) return;
    setState('saving');
    try {
      // Profile fields — CAMELCASE keys (mapped server-side); the picked logo
      // and cover go along as multipart files — see updateBrandProfile.
      const saved = await updateBrandProfile(
        brand.id,
        {
          storeName: name,
          companyName: company,
          tagline: tagline.trim().slice(0, BRAND_TAGLINE_MAX),
          description,
          phone,
          website,
          address,
        },
        logo.file,
        cover.file
      );
      // Swap the local object-URL previews for the stored, cache-busted files
      // the API answered with (so what the merchant sees is what the app gets).
      if (saved && typeof saved === 'object') {
        logo.setStored(saved.logo);
        cover.setStored(saved.cover_image);
        if (saved.tagline != null) setTagline(saved.tagline || '');
      }
      // IBAN + visibility go through the same endpoint but with snake_case keys.
      // Only send IBAN when the user actually entered/changed one — an empty
      // value would needlessly PUT `{iban:null}`.
      if (iban && iban.trim()) {
        await updateBrandIban(brand.id, iban);
      }
      if ((brand.isActive !== false) !== isActive) {
        await toggleBrandVisibility(brand.id, isActive);
      }
      setState('saved');
      // Reflect the saved values locally so the form doesn't appear to "do
      // nothing": the parent re-renders with the same brand id (stable key), so
      // ProfileTab won't remount — we already hold the latest values in state.
      logo.setStored(null);
      cover.setStored(null);
      onSaved && onSaved();
      setTimeout(dismissToast, 3200);
    } catch (err) {
      // Surface the real failure instead of silently swallowing it.
      // eslint-disable-next-line no-console
      console.error('Brand profile save failed:', err?.status, err?.data || err);
      setState('error');
      setTimeout(dismissToast, 3200);
    }
  }

  const disabled = !canEdit;

  return (
    <form className="panel-card" onSubmit={handleSave}>
      {/* Floating confirmation toast — a clearly visible save signal. */}
      {(state === 'saved' || state === 'error') && (
        <div
          className={`save-toast save-toast--${state} ${toastLeaving ? 'is-leaving' : ''}`}
          role="status"
        >
          {state === 'saved' ? (
            <>
              <Check2 size={18} />
              <span>{t('profile.saved')}</span>
            </>
          ) : (
            <>
              <XLg size={16} />
              <span>{t('profile.saveError')}</span>
            </>
          )}
        </div>
      )}

      <h2 className="panel-card__title">{t('profile.title')}</h2>

      <CoverField src={cover.preview} onPick={disabled ? null : cover.pick} label={t('profile.cover')} hint={t('profile.coverHint')} />
      <LogoField src={logo.preview} name={name} onPick={disabled ? null : logo.pick} label={t('profile.logo')} />

      <Field label={t('profile.name')}>
        <input value={name} onChange={(e) => setName(e.target.value)} disabled={disabled} />
      </Field>
      <Field label={t('profile.company')}>
        <input value={company} onChange={(e) => setCompany(e.target.value)} disabled={disabled} />
      </Field>
      <Field label={t('profile.tagline')}>
        <input
          value={tagline}
          maxLength={BRAND_TAGLINE_MAX}
          placeholder={t('profile.taglineHint')}
          onChange={(e) => setTagline(e.target.value)}
          disabled={disabled}
        />
      </Field>
      <Field label={t('profile.description')}>
        <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} disabled={disabled} />
      </Field>
      <div className="field-row">
        <Field label={t('profile.phone')}>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} disabled={disabled} />
        </Field>
        <Field label={t('profile.website')}>
          <input value={website} onChange={(e) => setWebsite(e.target.value)} disabled={disabled} />
        </Field>
      </div>
      <Field label={t('profile.address')}>
        <input value={address} onChange={(e) => setAddress(e.target.value)} disabled={disabled} />
      </Field>
      <Field label={t('profile.iban')}>
        <input value={iban} onChange={(e) => setIban(e.target.value)} placeholder="HU00 0000 0000 0000 0000 0000 0000" disabled={disabled} />
      </Field>

      <label className="toggle-field">
        <input
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          disabled={disabled}
        />
        <span>{t('profile.visibility')}</span>
      </label>

      {canEdit && (
        <div className="panel-card__foot">
          <button type="submit" className="btn-o btn-o--dark" disabled={state === 'saving'}>
            {state === 'saving' ? t('profile.saving') : t('profile.save')}
          </button>
        </div>
      )}
      {!canEdit && <div className="panel-card__muted">{t('profile.readOnly')}</div>}
    </form>
  );
}

// ── Products tab ────────────────────────────────────────────────────────────

/**
 * Link to a product in the merchant's own Shopify admin.
 *
 * `shopify_product_id` is stored as the numeric REST id, but a GID
 * ("gid://shopify/Product/123") turns up in some sync paths — take the trailing
 * digits either way rather than assuming one shape.
 */
function shopifyProductUrl(shopDomain, shopifyProductId) {
  if (!shopDomain || !shopifyProductId) return null;
  const numeric = String(shopifyProductId).match(/(\d+)\s*$/);
  if (!numeric) return null;
  return `https://${shopDomain}/admin/products/${numeric[1]}`;
}

function productStock(p) {
  const variants = Array.isArray(p?.variants) ? p.variants : [];
  if (variants.length === 0) return { total: null, tracked: 0, soldOut: 0, variants: 0 };

  let total = 0;
  let tracked = 0;
  let soldOut = 0;

  for (const variant of variants) {
    const raw = variant?.inventory_quantity;
    const parsed = typeof raw === 'number'
      ? raw
      : (raw == null || raw === '' ? null : parseInt(raw, 10));
    if (parsed == null || Number.isNaN(parsed)) continue;
    tracked += 1;
    total += Math.max(0, parsed);
    if (parsed <= 0) soldOut += 1;
  }

  return tracked === 0
    ? { total: null, tracked: 0, soldOut: 0, variants: variants.length }
    : { total, tracked, soldOut, variants: variants.length };
}

/** First variant price, formatted in the product's own currency. */
function productPrice(p) {
  const raw = p.price ?? p.price_data?.price ?? p.variants?.[0]?.price;
  if (raw == null || raw === '') return null;

  const value = Number(raw);
  if (!Number.isFinite(value)) return null;

  const currency = p.currency || p.price_data?.currency;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'HUF').toUpperCase(),
      maximumFractionDigits: 2,
    }).format(value);
  } catch (e) {
    // Unknown currency code — never let formatting blank out the price.
    return `${value.toLocaleString()}${currency ? ` ${currency.toUpperCase()}` : ''}`;
  }
}

// How often the Products tab asks how the running sync is doing. The work is a
// Shopify fetch plus per-product AI classification, so it moves in seconds, not
// milliseconds — polling faster would only add load for a bar that cannot show
// the difference.
const SYNC_POLL_MS = 2000;
const SYNC_IDLE_POLL_MS = 10000;

const LOW_STOCK_THRESHOLD = 5;

// The product list in whichever envelope the two endpoints use: a bare array
// (portal) or `{ products: [...] }` (embedded, paged).
function productListOf(list) {
  return Array.isArray(list) ? list : list?.data ?? list?.products ?? [];
}

// `canRelabel` (documents#188): the FAILED badge's Retry needs editProducts —
// the same guard the relabel endpoint enforces.
function ProductsTab({ brand, canSync, canRelabel = false }) {
  const { t } = useLocale();
  const { user } = useAuth();
  const devBrand = user?.environment === 'dev' && brand.environment === 'dev';
  const [products, setProducts] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [query, setQuery] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // #615 — the live sync counts, or null when nothing is running.
  const [progress, setProgress] = useState(null);
  // Bumped when a sync is kicked off, to restart the poll loop below.
  const [pollNonce, setPollNonce] = useState(0);
  const [disputes, setDisputes] = useState({});
  const [disputesNonce, setDisputesNonce] = useState(0);

  const visibleProducts = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!products || !q) return products;
    return products.filter((p) => (p.name || p.title || '').toLowerCase().includes(q));
  }, [products, query]);

  // #615 — the catalogue splits in two. Anything the AI classifier did not
  // call clothing is still the merchant's product and still synced, it just
  // never reaches the shopper-facing surfaces; showing it below the fold with
  // the reason attached beats leaving them to wonder what happened to it.
  //
  // Only an explicit `false` demotes a product: a null or missing is_clothing
  // means "not classified", which is not the same claim.
  const [clothing, nonClothing] = useMemo(() => {
    const included = [];
    const excluded = [];
    for (const p of visibleProducts ?? []) {
      (p.is_clothing === false ? excluded : included).push(p);
    }
    return [included, excluded];
  }, [visibleProducts]);

  useEffect(() => {
    let alive = true;
    setLoadError(false);
    fetchBrandProducts(brand.id)
      .then((list) => {
        if (alive) setProducts(productListOf(list));
      })
      // A failed request used to render as "no products yet" — indistinguishable
      // from an empty catalogue, so the merchant re-syncs and nothing changes.
      // Say the load failed and offer the retry.
      .catch(() => {
        if (!alive) return;
        setProducts([]);
        setLoadError(true);
      });
    return () => {
      alive = false;
    };
  }, [brand.id, reloadKey]);

  // Watches a running sync and retires itself when there is nothing to watch.
  //
  // Runs on mount too, not only after the sync button: a merchant who reloads
  // the tab mid-sync, or who started one from the embedded Shopify app, should
  // still see the bar.
  useEffect(() => {
    let alive = true;
    let timer = null;
    // Only a run we actually saw in flight should refresh the grid when it
    // ends. Without this, a 'completed' left over from an earlier sync would
    // trigger a pointless refetch on every mount.
    let sawSyncing = false;

    let inFlight = false;

    async function poll() {
      timer = null;
      let next = null;
      inFlight = true;
      try {
        next = await fetchSyncProgress(brand.id);
      } catch {
        // The bar is decoration. A failed poll takes it down; it must
        // never turn a working catalogue into an error screen.
        inFlight = false;
        if (!alive) return;
        setProgress(null);
        if (!document.hidden) timer = setTimeout(poll, SYNC_IDLE_POLL_MS);
        return;
      }
      inFlight = false;
      if (!alive) return;

      if (next?.status === 'syncing') {
        sawSyncing = true;
        setProgress(next);
        timer = setTimeout(poll, SYNC_POLL_MS);
        return;
      }

      setProgress(null);
      if (sawSyncing) {
        sawSyncing = false;
        setReloadKey((n) => n + 1);
      }
      if (!document.hidden) timer = setTimeout(poll, SYNC_IDLE_POLL_MS);
    }

    function onVisibilityChange() {
      if (document.hidden || !alive || inFlight) return;
      if (timer) clearTimeout(timer);
      poll();
    }

    poll();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [brand.id, pollNonce]);

  // documents#188 — while any product is still being labelled, re-read the
  // list every 5 s so the PENDING badges resolve without a manual refresh.
  // Only then: an idle catalogue is never polled. Paused while a sync runs
  // (the grid is hidden behind the progress bar, and the sync's own refetch
  // follows). A quiet re-read: the grid stays up, a failed poll changes
  // nothing — the tab must never flicker or blank because of it.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const labelingPending = useMemo(() => hasPendingLabeling(products), [products]);
  useLabelingPoll({
    active: labelingPending && !progress,
    intervalMs: LABELING_POLL_MS,
    onTick: () => {
      fetchBrandProducts(brand.id)
        .then((list) => { if (mounted.current) setProducts(productListOf(list)); })
        .catch(() => {});
    },
  });

  // A relabel was accepted (202): the response carries the queued block, so
  // the card reads PENDING at once and the poll above takes it from there.
  function handleRelabeled(productId, aiLabeling) {
    setProducts((list) => withLabeling(list, productId, aiLabeling));
  }

  useEffect(() => {
    let alive = true;
    fetchLabelDisputes(brand.id)
      .then((list) => { if (alive) setDisputes(latestDisputesByProduct(list)); })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [brand.id, disputesNonce]);

  function handleDisputed(productId, dispute) {
    setDisputes((map) => ({ ...map, [String(productId)]: dispute }));
    setDisputesNonce((n) => n + 1);
  }

  async function handleSync() {
    setSyncing(true);
    try {
      await syncProducts(brand.id);
      // The POST only starts the run — the worker is still asking Shopify for
      // the catalogue — so the first polls may come back with no total yet.
      // From here the progress bar is the sync's only report; there is no
      // "started" line to read and then have to dismiss.
      setPollNonce((n) => n + 1);
    } catch {
      // As before: if the sync will not start, the bar simply never appears.
    } finally {
      setSyncing(false);
    }
  }

  // One product card. Both grids render the same thing — the excluded list
  // below differs in its wrapper and its opacity, never in what a product
  // looks like.
  function renderCell(p, i) {
    const img = (p.images && (p.images[0]?.src || p.images[0])) || p.image || null;
    const price = productPrice(p);
    const adminUrl = shopifyProductUrl(brand.website, p.shopify_product_id);
    const status = p.shopify_status || p.product_status;
    const inactive = brand.isActive === false || String(status || '').toUpperCase() === 'BRAND_INACTIVE';
    const stock = productStock(p);
    const out = stock.total === 0;
    const low = !out && stock.total !== null && stock.total <= LOW_STOCK_THRESHOLD;

    return (
      <div className={`product-cell${out ? ' is-sold-out' : ''}`} key={p.id || i}>
        <div className="product-cell__img">
          {img ? <img src={img} alt="" loading="lazy" /> : <span aria-hidden="true">▢</span>}
          {inactive ? (
            <span className="product-cell__status is-inactive">{t('products.inactive')}</span>
          ) : status && status !== 'ACTIVE' && (
            <span className="product-cell__status">{status}</span>
          )}
          {out && <span className="product-cell__sold-out">{t('products.soldOut')}</span>}
          {devBrand && <span className="product-cell__dev">{t('shell.envDev')}</span>}
        </div>

        <div className="product-cell__body">
          <div className="product-cell__name" title={p.name || p.title || ''}>
            {p.name || p.title || '—'}
          </div>
          {price && <div className="product-cell__price">{price}</div>}
          <div className={`product-cell__stock${out ? ' is-out' : ''}${low ? ' is-low' : ''}`}>
            {stock.total === null
              ? t('products.stockUntracked')
              : t('products.stockCount', { n: stock.total })}
            {stock.variants > 1 && stock.soldOut > 0 && stock.total !== null && (
              <span className="product-cell__stock-variants">
                {t('products.stockVariantsOut', { out: stock.soldOut, total: stock.variants })}
              </span>
            )}
          </div>
          {/* documents#188 — AI labelling status; nothing for a never-queued row. */}
          <LabelingBadge
            product={p}
            brandId={brand.id}
            canRelabel={canRelabel}
            onRelabeled={handleRelabeled}
          />
          <LabelDispute
            product={p}
            brandId={brand.id}
            dispute={disputes[String(p.id)]}
            canDispute={canRelabel}
            onDisputed={handleDisputed}
          />
        </div>

        {adminUrl && (
          <div className="product-cell__foot">
            <a
              className="product-cell__link"
              href={adminUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span>{t('products.openInShopify')}</span>
              <BoxArrowUpRight size={12} />
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="panel-card">
      <div className="panel-card__row">
        <h2 className="panel-card__title">
          {t('products.title')}
          {products?.length > 0 && <span className="product-count">{products.length}</span>}
        </h2>
        {canSync && (
          <button className="btn-o btn-o--brand" onClick={handleSync} disabled={syncing}>
            {syncing ? t('products.syncing') : t('products.sync')}
          </button>
        )}
      </div>
      {/* #615 — while a sync runs the bar is the whole panel. What is in the
          grid is the catalogue as it was BEFORE this run, so showing it next to
          a bar that says it is being rewritten offers the merchant a list they
          cannot trust. Until the worker has heard back from Shopify there is no
          total to divide by either, so the bar says it is preparing rather than
          guessing at a length. */}
      {progress ? (
        <div className="sync-progress sync-progress--solo">
          <div className="sync-progress__head">
            <span className="sync-progress__label">{t('products.syncProgress')}</span>
            <span className="sync-progress__count">
              {progress.total
                ? t('products.syncCount', {
                    done: progress.processed,
                    total: progress.total,
                  })
                : t('products.syncPreparing')}
            </span>
          </div>
          <div
            className={`sync-progress__track${progress.total ? '' : ' is-indeterminate'}`}
            role="progressbar"
            aria-label={t('products.syncProgress')}
            aria-valuemin={0}
            {...(progress.total
              ? { 'aria-valuemax': progress.total, 'aria-valuenow': progress.processed }
              : {})}
          >
            <div
              className="sync-progress__fill"
              style={
                progress.total
                  ? { width: `${Math.round((progress.processed / progress.total) * 100)}%` }
                  : undefined
              }
            />
          </div>
        </div>
      ) : (
        <>
          {products?.length > 0 && (
            <div className="product-search">
              <Search size={14} className="product-search__icon" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('products.search')}
                aria-label={t('products.search')}
              />
            </div>
          )}

          {products === null ? (
            <div className="panel-card__muted">{t('shell.loading')}</div>
          ) : loadError ? (
            <div className="panel-card__muted">
              {t('products.loadError')}{' '}
              <button
                className="btn-o btn-o--ghost btn-o--sm"
                onClick={() => setReloadKey((n) => n + 1)}
              >
                {t('products.retry')}
              </button>
            </div>
          ) : products.length === 0 ? (
            <div className="panel-card__muted">{t('products.empty')}</div>
          ) : visibleProducts.length === 0 ? (
            <div className="panel-card__muted">{t('products.noMatch')}</div>
          ) : (
            <>
              {clothing.length > 0 && (
                <div className="product-grid">{clothing.map(renderCell)}</div>
              )}

              {/* #615 — everything the classifier did not call clothing, kept out
                  of the catalogue above but not out of sight. Dimmed and below a
                  rule, because it is still the merchant's product and still
                  synced; it just never reaches a shopper. */}
              {nonClothing.length > 0 && (
                <section className="excluded">
                  <h3 className="excluded__title">
                    {t('products.excludedTitle')}
                    <span className="product-count">{nonClothing.length}</span>
                  </h3>
                  <p className="excluded__hint">{t('products.excludedHint')}</p>
                  <div className="product-grid excluded__grid">
                    {nonClothing.map(renderCell)}
                  </div>
                </section>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
// ── Drops tab ───────────────────────────────────────────────────────────────

function fmtDropDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const DROPPABLE_STATUSES = ['DRAFT', 'UNLISTED'];

function toLocalInputValue(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function productImage(p) {
  const imgs = Array.isArray(p.images) ? p.images : [];
  return imgs[0]?.src || imgs[0] || p.image || null;
}

function NewDropForm({ brand, busyProductIds, onCreated, onClose }) {
  const { t } = useLocale();
  const [products, setProducts] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [query, setQuery] = useState('');
  const [dropDate, setDropDate] = useState('');
  const [exclusive, setExclusive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    fetchBrandProducts(brand.id)
      .then((list) => {
        if (!alive) return;
        const droppable = productListOf(list).filter((p) =>
          p.is_clothing === true
          && DROPPABLE_STATUSES.includes(String(p.product_status || p.shopify_status || '').toUpperCase())
          && !busyProductIds.has(String(p.shopify_product_id))
        );
        setProducts(droppable);
      })
      .catch(() => alive && setProducts([]));
    return () => {
      alive = false;
    };
  }, [brand.id, busyProductIds]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!products || !q) return products;
    return products.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [products, query]);

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const minDate = toLocalInputValue(new Date(Date.now() + 5 * 60 * 1000));
  const dateValid = dropDate && new Date(dropDate).getTime() > Date.now();
  const valid = selected.size > 0 && dateValid;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await createDrops(brand.id, {
        productIds: [...selected],
        dropDate: new Date(dropDate).toISOString(),
        exclusive,
      });
      onCreated(result);
    } catch {
      setError(t('drops.form.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  const selectedProducts = (products || []).filter((p) => selected.has(p.id));

  return (
    <form className="drop-form" onSubmit={handleSubmit}>
      <div className="drop-form__head">
        <h3>{t('drops.form.title')}</h3>
        <button type="button" className="drop-form__close" onClick={onClose} aria-label={t('drops.form.close')}>
          <XLg size={14} />
        </button>
      </div>

      <section className="drop-step">
        <div className="drop-step__title">
          <span className="drop-step__num">1</span>
          {t('drops.form.products')}
        </div>
        <p className="drop-form__hint">{t('drops.form.productsHint')}</p>
        <div className="drop-form__search">
          <Search size={14} />
          <input
            value={query}
            placeholder={t('drops.form.search')}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {products === null ? (
          <div className="panel-card__muted">{t('shell.loading')}</div>
        ) : products.length === 0 ? (
          <div className="panel-card__muted">{t('drops.form.noProducts')}</div>
        ) : (
          <div className="drop-pick">
            {shown.map((p) => {
              const img = productImage(p);
              const checked = selected.has(p.id);
              return (
                <label className={`drop-pick__item${checked ? ' is-checked' : ''}`} key={p.id}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(p.id)} />
                  <span className="drop-pick__img">
                    {img ? <img src={img} alt="" loading="lazy" /> : <BoxSeam size={16} />}
                  </span>
                  <span className="drop-pick__name">{p.name || '—'}</span>
                  <span className="drop-pick__status">
                    {String(p.product_status || p.shopify_status || '').toUpperCase()}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </section>

      <section className="drop-step">
        <div className="drop-step__title">
          <span className="drop-step__num">2</span>
          {t('drops.form.date')}
        </div>
        <input
          className="drop-form__date"
          type="datetime-local"
          value={dropDate}
          min={minDate}
          onChange={(e) => setDropDate(e.target.value)}
        />
      </section>

      <section className="drop-step">
        <div className="drop-step__title">
          <span className="drop-step__num">3</span>
          {t('drops.form.where')}
        </div>
        <div className="drop-mode" role="radiogroup">
          <button
            type="button"
            role="radio"
            aria-checked={exclusive}
            className={`drop-mode__card${exclusive ? ' is-selected' : ''}`}
            onClick={() => setExclusive(true)}
          >
            <span className="drop-mode__icon"><Stars size={18} /></span>
            <span className="drop-mode__text">
              <strong>{t('drops.form.modeExclusive')}</strong>
              <span>{t('drops.form.modeExclusiveHint')}</span>
            </span>
            {exclusive && <CheckCircleFill className="drop-mode__check" size={18} />}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={!exclusive}
            className={`drop-mode__card${!exclusive ? ' is-selected' : ''}`}
            onClick={() => setExclusive(false)}
          >
            <span className="drop-mode__icon"><Shop size={18} /></span>
            <span className="drop-mode__text">
              <strong>{t('drops.form.modeBoth')}</strong>
              <span>{t('drops.form.modeBothHint')}</span>
            </span>
            {!exclusive && <CheckCircleFill className="drop-mode__check" size={18} />}
          </button>
        </div>
      </section>

      <div className="drop-summary">
        <div className="drop-summary__items">
          <span>{t('drops.form.selected', { n: selectedProducts.length })}</span>
          <span>{dateValid ? fmtDropDate(new Date(dropDate)) : t('drops.form.noDate')}</span>
          <span>{exclusive ? t('drops.form.modeExclusive') : t('drops.form.modeBoth')}</span>
        </div>
        <button type="submit" className="btn-o btn-o--dark" disabled={!valid || submitting}>
          {submitting ? t('drops.form.creating') : t('drops.form.create')}
        </button>
      </div>

      {error && <div className="dash__notice dash__notice--error">{error}</div>}
    </form>
  );
}

function DropsTab({ brand, canEdit = false }) {
  const { t } = useLocale();
  const [drops, setDrops] = useState(null);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState('');
  const [busyDrop, setBusyDrop] = useState(null);
  // The scheduled drop waiting for a yes in the confirm dialog.
  const [confirmDrop, setConfirmDrop] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchBrandDrops(brand.id)
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        if (alive) setDrops(arr);
      })
      .catch(() => alive && setDrops([]));
    return () => {
      alive = false;
    };
  }, [brand.id, reloadKey]);

  const busyProductIds = useMemo(
    () => new Set((drops || [])
      .filter((d) => d.status === 'scheduled' || d.status === 'active')
      .map((d) => String(d.product_id))),
    [drops]
  );

  function handleCreated(result) {
    const created = result?.created?.length ?? 0;
    const skipped = result?.skipped?.length ?? 0;
    setCreating(false);
    setNotice(skipped > 0
      ? t('drops.form.createdSkipped', { n: created, skipped })
      : t('drops.form.created', { n: created }));
    setReloadKey((k) => k + 1);
  }

  async function handleCancel(drop) {
    setBusyDrop(drop.id);
    try {
      await cancelDrop(drop.id);
      setReloadKey((k) => k + 1);
    } catch {
      setNotice(t('drops.cancelFailed'));
    } finally {
      setBusyDrop(null);
    }
  }

  return (
    <div className="panel-card">
      <div className="panel-card__row">
        <h2 className="panel-card__title">{t('drops.title')}</h2>
        {canEdit && !creating && (
          <button className="btn-o btn-o--brand" onClick={() => { setNotice(''); setCreating(true); }}>
            <PlusLg size={14} style={{ marginRight: 6 }} />
            {t('drops.new')}
          </button>
        )}
      </div>

      {notice && <div className="dash__notice">{notice}</div>}

      {creating && (
        <NewDropForm
          brand={brand}
          busyProductIds={busyProductIds}
          onCreated={handleCreated}
          onClose={() => setCreating(false)}
        />
      )}

      {drops === null ? (
        <div className="panel-card__muted">{t('shell.loading')}</div>
      ) : drops.length === 0 ? (
        <div className="panel-card__muted">{t('drops.empty')}</div>
      ) : (
        <div className="drop-list">
          {drops.map((d, i) => {
            const imgs = Array.isArray(d.product_images) ? d.product_images : [];
            const img = imgs[0]?.src || imgs[0] || null;
            const status = d.status || 'scheduled';
            const price =
              d.price != null && Number(d.price) > 0
                ? `${Number(d.price).toLocaleString()} ${d.currency || ''}`.trim()
                : null;
            return (
              <div className="drop-row" key={d.id || i}>
                <div className="drop-row__img">
                  {img ? <img src={img} alt="" /> : <Lightning size={18} />}
                </div>
                <div className="drop-row__main">
                  <div className="drop-row__name">{d.product_name || '—'}</div>
                  <div className="drop-row__meta">
                    {fmtDropDate(d.drop_date)}
                    {price && <span className="drop-row__price">{price}</span>}
                    {d.exclusive !== false && (
                      <span className="drop-row__exclusive">{t('drops.exclusiveBadge')}</span>
                    )}
                  </div>
                </div>
                <span className={`drop-row__status is-${status}`}>
                  {t(`drops.status.${status}`)}
                </span>
                {canEdit && status === 'scheduled' && (
                  <button
                    className="btn-o btn-o--ghost btn-o--sm"
                    onClick={() => setConfirmDrop(d)}
                    disabled={busyDrop === d.id}
                  >
                    {t('drops.cancel')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <ConfirmDialog
        open={!!confirmDrop}
        title={t('drops.cancelConfirmTitle')}
        body={confirmDrop ? t('drops.cancelConfirmBody', { name: confirmDrop.product_name || '—' }) : ''}
        cancelLabel={t('confirm.back')}
        confirmLabel={t('drops.cancelConfirm')}
        onCancel={() => setConfirmDrop(null)}
        onConfirm={() => {
          const d = confirmDrop;
          setConfirmDrop(null);
          if (d) handleCancel(d);
        }}
      />
    </div>
  );
}

// ── Creator campaigns tab (REAL — the brand's briefs, applicants, new brief) ─
//
// This is the brand side of the creator collaboration, wired to the live
// backend (/brands/:brandId/campaigns …). Three views: list → detail
// (applicants) → new. All endpoints require the `manageCampaigns` permission.

function fmtFollowers(n) {
  if (n == null) return '';
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

function CampaignsTab({ brand, canEdit }) {
  const { t } = useLocale();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('list'); // list | detail | new
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  function loadList() {
    setLoading(true);
    setError('');
    fetchBrandCampaigns(brand.id)
      .then((list) => setCampaigns(Array.isArray(list) ? list : []))
      .catch((e) => setError(e?.message || t('campaigns.error')))
      .finally(() => setLoading(false));
  }
  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.id]);

  function openDetail(id) {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    setView('detail');
    fetchBrandCampaign(brand.id, id)
      .then((c) => setDetail(c))
      .catch((e) => setError(e?.message || t('campaigns.error')))
      .finally(() => setDetailLoading(false));
  }

  async function handleCreate(draft, setFormError) {
    try {
      await createBrandCampaign(brand.id, draft);
      setView('list');
      loadList();
    } catch (e) {
      if (setFormError) setFormError(e?.message || t('campaigns.error'));
    }
  }

  // Select / reject a creator for a brief, then refetch the detail.
  async function toggleSelect(campaignId, userId, currentlySelected, onError) {
    try {
      if (currentlySelected) {
        await rejectCampaignApplicant(brand.id, campaignId, userId);
      } else {
        await selectCampaignApplicant(brand.id, campaignId, userId);
      }
      const fresh = await fetchBrandCampaign(brand.id, campaignId);
      setDetail(fresh);
      loadList();
    } catch (e) {
      // 409 → slots full; surface the server message.
      if (onError) onError(e?.message || t('campaigns.error'));
    }
  }

  if (view === 'new') {
    return (
      <NewCampaignForm
        onCancel={() => setView('list')}
        onCreate={handleCreate}
      />
    );
  }

  if (view === 'detail') {
    return (
      <CampaignDetail
        campaign={detail}
        loading={detailLoading}
        canEdit={canEdit}
        onBack={() => setView('list')}
        onToggleSelect={(userId, currentlySelected, onError) =>
          toggleSelect(selectedId, userId, currentlySelected, onError)
        }
      />
    );
  }

  return (
    <div className="panel-card">
      <div className="panel-card__row">
        <h2 className="panel-card__title">{t('campaigns.title')}</h2>
        {canEdit && (
          <button className="btn-o btn-o--brand" onClick={() => setView('new')}>
            <PlusLg size={15} style={{ marginRight: 6 }} />
            {t('campaigns.new')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="panel-card__muted">{t('campaigns.loading')}</div>
      ) : error ? (
        <div className="panel-card__muted">{error}</div>
      ) : campaigns.length === 0 ? (
        <div className="panel-card__muted">{t('campaigns.empty')}</div>
      ) : (
        <div className="campaign-list">
          {campaigns.map((c) => {
            const applicants = c.applicants || [];
            const selectedCount = applicants.filter((a) => a.selected).length;
            return (
              <button
                key={c.id}
                className="campaign-row"
                onClick={() => openDetail(c.id)}
              >
                <div className="campaign-row__main">
                  <div className="campaign-row__title">{c.title}</div>
                  <div className="campaign-row__meta">
                    <span className={`campaign-row__status is-${c.status}`}>
                      {t(`campaigns.status.${c.status}`)}
                    </span>
                    <span className="campaign-row__dot">·</span>
                    <span>
                      {t('campaigns.applicantsCount', { count: applicants.length })}
                    </span>
                    <span className="campaign-row__dot">·</span>
                    <span>
                      {t('campaigns.selectedCount', {
                        done: selectedCount,
                        total: c.slotsNeeded,
                      })}
                    </span>
                  </div>
                </div>
                <ChevronRight size={18} className="campaign-row__chev" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CampaignDetail({ campaign, loading, canEdit, onBack, onToggleSelect }) {
  const { t } = useLocale();
  const [actionError, setActionError] = useState('');

  if (loading || !campaign) {
    return (
      <div className="panel-card">
        <button className="link-back" onClick={onBack}>
          ‹ {t('campaigns.back')}
        </button>
        <div className="panel-card__muted">{t('campaigns.loading')}</div>
      </div>
    );
  }

  const applicants = campaign.applicants || [];
  const selectedCount = applicants.filter((a) => a.selected).length;

  return (
    <div className="panel-card">
      <button className="link-back" onClick={onBack}>
        ‹ {t('campaigns.back')}
      </button>

      <div className="panel-card__row">
        <h2 className="panel-card__title">{campaign.title}</h2>
        <span className={`campaign-row__status is-${campaign.status}`}>
          {t(`campaigns.status.${campaign.status}`)}
        </span>
      </div>

      <p className="campaign-detail__desc">{campaign.description}</p>

      <div className="campaign-detail__facts">
        <span className="campaign-detail__fact">
          <Calendar3 size={14} />
          {t('campaigns.applyDeadline')}: {campaign.applyDeadline}
        </span>
        <span className="campaign-detail__fact">
          <Calendar3 size={14} />
          {t('campaigns.deliveryDeadline')}: {campaign.deliveryDeadline}
        </span>
        <span className="campaign-detail__fact">
          {t('campaigns.slots', { count: campaign.slotsNeeded })}
        </span>
        <span className="campaign-detail__fact">
          {t('campaigns.videos', { count: campaign.requiredVideos })}
        </span>
      </div>

      <div className="campaign-detail__applicants-head">
        <span>{t('campaigns.applicants')}</span>
        <span className="campaign-detail__selected">
          {t('campaigns.selectedCount', {
            done: selectedCount,
            total: campaign.slotsNeeded,
          })}
        </span>
      </div>

      {actionError && (
        <div className="panel-card__muted">{actionError}</div>
      )}

      {applicants.length === 0 ? (
        <div className="panel-card__muted">{t('campaigns.noApplicants')}</div>
      ) : (
        <div className="applicant-list">
          {applicants.map((a) => (
            <div
              className={`applicant-row ${a.selected ? 'is-selected' : ''}`}
              key={a.userId}
            >
              <span className="applicant-row__avatar">
                {(a.username || '?')[0].toUpperCase()}
              </span>
              <div className="applicant-row__id">
                <div className="applicant-row__name">@{a.username}</div>
                <div className="applicant-row__sub">
                  {fmtFollowers(a.followers)} · {a.platform}
                </div>
              </div>
              {a.selected && (
                <span className="applicant-row__badge">
                  <Check2 size={13} /> {t('campaigns.selected')}
                </span>
              )}
              {canEdit && (
                <button
                  className={`btn-o btn-o--sm ${a.selected ? 'btn-o--ghost' : 'btn-o--dark'}`}
                  onClick={() => {
                    setActionError('');
                    onToggleSelect(a.userId, a.selected, setActionError);
                  }}
                >
                  {a.selected ? t('campaigns.deselect') : t('campaigns.select')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewCampaignForm({ onCancel, onCreate }) {
  const { t } = useLocale();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slotsNeeded, setSlotsNeeded] = useState(3);
  const [requiredVideos, setRequiredVideos] = useState(1);
  const [payment, setPayment] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [applyDeadline, setApplyDeadline] = useState('');
  const [deliveryDeadline, setDeliveryDeadline] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const valid = title.trim() && description.trim();

  function submit(e) {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError('');
    Promise.resolve(
      onCreate(
        {
          title: title.trim(),
          description: description.trim(),
          slotsNeeded: Number(slotsNeeded) || 1,
          requiredVideos: Number(requiredVideos) || 1,
          payment: payment.trim(),
          amount: amount === '' ? null : Number(amount),
          currency,
          applyDeadline,
          deliveryDeadline,
        },
        setError
      )
    ).finally(() => setSubmitting(false));
  }

  return (
    <form className="panel-card" onSubmit={submit}>
      <button type="button" className="link-back" onClick={onCancel}>
        ‹ {t('campaigns.back')}
      </button>
      <h2 className="panel-card__title">{t('campaigns.newTitle')}</h2>

      <Field label={t('campaigns.fTitle')}>
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label={t('campaigns.fDescription')}>
        <textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <div className="field-row">
        <Field label={t('campaigns.fSlots')}>
          <input type="number" min="1" value={slotsNeeded} onChange={(e) => setSlotsNeeded(e.target.value)} />
        </Field>
        <Field label={t('campaigns.fVideos')}>
          <input type="number" min="1" value={requiredVideos} onChange={(e) => setRequiredVideos(e.target.value)} />
        </Field>
      </div>
      <Field label={t('campaigns.fPayment')}>
        <input value={payment} onChange={(e) => setPayment(e.target.value)} placeholder={t('campaigns.fPaymentPh')} />
      </Field>
      <div className="field-row">
        <Field label={t('campaigns.fAmount')}>
          <input
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={t('campaigns.fAmountPh')}
          />
        </Field>
        <Field label={t('campaigns.fCurrency')}>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            <option value="EUR">EUR</option>
            <option value="HUF">HUF</option>
          </select>
        </Field>
      </div>
      <div className="field-row">
        <Field label={t('campaigns.applyDeadline')}>
          <input type="date" value={applyDeadline} onChange={(e) => setApplyDeadline(e.target.value)} />
        </Field>
        <Field label={t('campaigns.deliveryDeadline')}>
          <input type="date" value={deliveryDeadline} onChange={(e) => setDeliveryDeadline(e.target.value)} />
        </Field>
      </div>

      {error && <div className="panel-card__muted">{error}</div>}

      <div className="panel-card__foot">
        <button type="submit" className="btn-o btn-o--dark" disabled={!valid || submitting}>
          {submitting ? t('campaigns.creating') : t('campaigns.create')}
        </button>
        <button type="button" className="btn-o btn-o--ghost" onClick={onCancel}>
          {t('campaigns.cancel')}
        </button>
      </div>
    </form>
  );
}

// ── Stats tab (real: overview + revenue + payouts) ──────────────────────────

function StatsTab({ brand }) {
  const { t } = useLocale();
  const [overview, setOverview] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    fetchStatsOverview(brand.id).then((d) => d?.success && setOverview(d)).catch(() => {});
  }, [brand.id]);

  useEffect(() => {
    fetchRevenue(brand.id, period).then((d) => d?.success && setRevenue(d)).catch(() => {});
  }, [brand.id, period]);

  const cur = (n) => formatMoney(n, overview?.currency);

  return (
    <div className="panel-card">
      <h2 className="panel-card__title">{t('stats.title')}</h2>

      {/* Balance / earnings */}
      <div className="stat-cards">
        <StatCard label={t('stats.available')} value={cur(overview?.balance?.available)} />
        <StatCard label={t('stats.totalEarned')} value={cur(overview?.balance?.total_earned)} />
        <StatCard label={t('stats.thisMonth')} value={cur(overview?.this_month?.revenue)} />
        <StatCard label={t('stats.orders')} value={overview?.this_month?.orders ?? '—'} />
      </div>

      {/* Revenue period selector + total */}
      <div className="stat-block">
        <div className="stat-block__head">
          <span>{t('stats.revenue')}</span>
          <select value={period} onChange={(e) => setPeriod(e.target.value)}>
            {['day', 'week', 'month', 'year', 'all'].map((p) => (
              <option key={p} value={p}>
                {t(`stats.period.${p}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="stat-block__big">{cur(revenue?.stats?.total_revenue)}</div>
        {revenue?.chart_data?.length > 0 && (
          <MiniChart data={revenue.chart_data} />
        )}
      </div>
    </div>
  );
}


// ── Orders tab — what this brand has to fulfil ───────────────────────────────
// A safety net beside the Shopify store, not a replacement for it. The Shopify
// order is where a merchant actually packs and ships; this is where they can
// check what Outfino recorded — including on staging, where no Shopify order is
// created at all and the store would otherwise look empty after a real purchase.
//
// Every figure here is this brand's own slice. An order can contain another
// brand's products; those never appear.
function OrdersTab({ brand }) {
  const { t } = useLocale();
  const [orders, setOrders] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoadError(false);
    fetchBrandOrders(brand.id)
      .then((d) => {
        if (!alive) return;
        setOrders(d?.orders ?? d?.message?.orders ?? []);
      })
      .catch(() => {
        if (!alive) return;
        setOrders([]);
        setLoadError(true);
      });
    return () => { alive = false; };
  }, [brand.id]);

  function money(value, currency) {
    if (value == null) return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (currency || 'HUF').toUpperCase(),
        maximumFractionDigits: 0,
      }).format(n);
    } catch (e) {
      return `${n.toLocaleString()} ${currency || ''}`.trim();
    }
  }

  function when(iso) {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch (e) {
      return '—';
    }
  }

  function addressLines(address) {
    if (!address || typeof address !== 'object') return [];
    return [
      address.customerName || address.customer_name,
      address.address1,
      [address.zip, address.city].filter(Boolean).join(' '),
      address.country,
    ].filter((v) => v && String(v).trim() !== '');
  }

  if (orders === null) {
    return <div className="panel-card panel-card__muted">{t('orders.loading')}</div>;
  }

  if (loadError) {
    return <div className="panel-card panel-card__muted">{t('orders.loadError')}</div>;
  }

  if (orders.length === 0) {
    return <div className="panel-card panel-card__muted">{t('orders.empty')}</div>;
  }

  return (
    <div className="panel-card">
      <h2 className="panel-card__title">
        {t('orders.title')}
        <span className="product-count">{orders.length}</span>
      </h2>
      <p className="orders__hint">{t('orders.hint')}</p>

      <div className="orders">
        {orders.map((o) => {
          const open = openId === o.order_id;
          const units = o.items.reduce((sum, i) => sum + (Number(i.quantity) || 1), 0);

          return (
            <div key={o.order_id} className={`order ${open ? 'is-open' : ''}`}>
              <button
                type="button"
                className="order__head"
                onClick={() => setOpenId(open ? null : o.order_id)}
              >
                <span className="order__number">{o.order_number}</span>
                <span className="order__meta">
                  {when(o.placed_at)} · {t('orders.units').replace('{n}', String(units))}
                </span>
                <span className="order__amount">{money(o.brand_amount, o.currency)}</span>
              </button>

              {open && (
                <div className="order__body">
                  <div className="order__items">
                    {o.items.map((i, idx) => (
                      <div key={`${o.order_id}-${idx}`} className="order__item">
                        {i.image
                          ? <img className="order__thumb" src={i.image} alt="" />
                          : <span className="order__thumb order__thumb--ph" />}
                        <span className="order__item-name">{i.name || '—'}</span>
                        <span className="order__item-qty">{i.quantity}×</span>
                        <span className="order__item-price">
                          {money((Number(i.price) || 0) * (Number(i.quantity) || 1), i.currency || o.currency)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <dl className="order__facts">
                    <dt>{t('orders.subtotal')}</dt>
                    <dd>{money(o.product_subtotal, o.currency)}</dd>

                    <dt>{t('orders.shipping')}</dt>
                    <dd>{money(o.shipping_cost, o.currency)}</dd>

                    {/* The brand's take after commission — the number that
                        reaches their balance, stated so they never have to
                        work it out from the subtotal and a percentage. */}
                    <dt>{t('orders.yourShare').replace('{n}', String(o.commission_percentage))}</dt>
                    <dd className="order__share">{money(o.brand_amount, o.currency)}</dd>

                    {o.shipping_method && (
                      <>
                        <dt>{t('orders.method')}</dt>
                        <dd>{o.shipping_method}</dd>
                      </>
                    )}

                    {o.shipping_attributes?.map((a) => (
                      <Fragment key={a.key}>
                        <dt>{a.key}</dt>
                        <dd>{a.value}</dd>
                      </Fragment>
                    ))}

                    <dt>{t('orders.deliverTo')}</dt>
                    <dd>
                      {addressLines(o.shipping_address).map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                      {o.customer_email && <div className="order__muted">{o.customer_email}</div>}
                      {o.customer_phone && <div className="order__muted">{o.customer_phone}</div>}
                    </dd>

                    <dt>{t('orders.shopifyOrder')}</dt>
                    <dd>
                      {o.shopify_order_id
                        ? <span className="order__mono">{o.shopify_order_id}</span>
                        : (
                          <>
                            <span className="order__muted">{t('orders.noShopifyOrder')}</span>
                            {o.shopify_note && <div className="order__mono">{o.shopify_note}</div>}
                          </>
                        )}
                    </dd>
                  </dl>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
// ── Payouts tab (available balance + payout history + request) ──────────────

// Payout statuses come from the API as enum values (pending, completed …).
function payoutStatusLabel(t, status) {
  const key = `payouts.status.${status}`;
  const label = t(key);
  return label === key ? status : label;
}

function PayoutsTab({ brand }) {
  const { t } = useLocale();
  const [overview, setOverview] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [notice, setNotice] = useState('');
  const [confirmPayout, setConfirmPayout] = useState(false);

  function loadPayouts() {
    fetchPayouts(brand.id).then((d) => d?.success && setPayouts(d)).catch(() => {});
  }
  useEffect(() => {
    fetchStatsOverview(brand.id).then((d) => d?.success && setOverview(d)).catch(() => {});
    loadPayouts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.id]);

  const currency = (
    payouts?.currency ||
    overview?.currency ||
    ''
  ).toUpperCase();
  const cur = (n) => formatMoney(n, currency);

  // Available balance: prefer the overview's balance, fall back to payouts.
  const available =
    overview?.balance?.available ?? payouts?.current_balance ?? null;
  // One open request at a time: while a payout is pending, the balance it
  // covers must not be requested again.
  const hasPending = Number(payouts?.pending_payouts) > 0;
  const canRequest = Number(available) > 0 && !requesting && !hasPending;

  // Commission %. The API's amounts (brand_amount / total_earned / balance) are
  // already NET — i.e. after Outfino's commission is deducted. We reconstruct the
  // gross lifetime revenue and the commission taken so the split is transparent:
  //   net = gross * (1 - c/100)  ⇒  gross = net / (1 - c/100)
  const commissionPct = overview?.commission_percentage ?? null;
  const netEarned =
    overview?.balance?.total_earned ?? payouts?.total_earned ?? null;
  const hasSplit =
    commissionPct != null && commissionPct > 0 && commissionPct < 100 && netEarned != null;
  const grossEarned = hasSplit ? netEarned / (1 - commissionPct / 100) : null;
  const commissionTaken = hasSplit ? grossEarned - netEarned : null;

  async function handleRequest() {
    if (!canRequest) return;
    setRequesting(true);
    setNotice('');
    try {
      await requestPayout(brand.id, Number(available));
      setNotice(t('payouts.requested'));
      loadPayouts();
      fetchStatsOverview(brand.id).then((d) => d?.success && setOverview(d)).catch(() => {});
    } catch {
      setNotice(t('payouts.requestError'));
    } finally {
      setRequesting(false);
    }
  }

  const recent = payouts?.recent_payouts || [];

  return (
    <div className="panel-card">
      <h2 className="panel-card__title">{t('payouts.title')}</h2>

      {/* Available balance hero + request button */}
      <div className="balance-hero">
        <div className="balance-hero__main">
          <div className="balance-hero__label">{t('stats.available')}</div>
          <div className="balance-hero__value">{cur(available)}</div>
          {commissionPct != null && commissionPct > 0 && (
            <div className="balance-hero__note">
              {t('payouts.commissionNote', { pct: commissionPct })}
            </div>
          )}
        </div>
        <button
          className="btn-o btn-o--brand"
          onClick={() => setConfirmPayout(true)}
          disabled={!canRequest}
        >
          {requesting ? t('payouts.requesting') : t('payouts.request')}
        </button>
      </div>
      {notice && <div className="panel-card__ok">{notice}</div>}
      {hasPending && !notice && <div className="panel-card__muted">{t('payouts.pendingBlocks')}</div>}
      <ConfirmDialog
        open={confirmPayout}
        title={t('payouts.confirmTitle')}
        body={t('payouts.confirmBody', { amount: cur(available) })}
        cancelLabel={t('confirm.back')}
        confirmLabel={t('payouts.request')}
        onCancel={() => setConfirmPayout(false)}
        onConfirm={() => {
          setConfirmPayout(false);
          handleRequest();
        }}
      />

      {/* Revenue breakdown + pending, side by side */}
      <div className="payout-cols">
        {hasSplit && (
          <div className="breakdown">
            <div className="breakdown__title">{t('payouts.breakdownTitle')}</div>
            <div className="breakdown__row">
              <span>{t('payouts.gross')}</span>
              <span className="breakdown__amount">{cur(grossEarned)}</span>
            </div>
            <div className="breakdown__row breakdown__row--minus">
              <span>{t('payouts.commissionLine', { pct: commissionPct })}</span>
              <span className="breakdown__amount">− {cur(commissionTaken)}</span>
            </div>
            <div className="breakdown__row breakdown__row--net">
              <span>{t('payouts.net')}</span>
              <span className="breakdown__amount">{cur(netEarned)}</span>
            </div>
          </div>
        )}
        <div className="pending-card">
          <div className="breakdown__title">{t('payouts.pending')}</div>
          <div className="pending-card__value">{cur(payouts?.pending_payouts)}</div>
        </div>
      </div>

      {/* Payout history */}
      <div className="stat-block">
        <div className="stat-block__head">
          <span>{t('payouts.history')}</span>
        </div>
        {recent.length > 0 ? (
          <div className="payout-list">
            {recent.map((p) => (
              <div className="payout-row" key={p.id}>
                <span className="payout-row__amount">{cur(p.amount)}</span>
                <span className={`payout-row__status is-${p.status}`}>{payoutStatusLabel(t, p.status)}</span>
                <span className="payout-row__date">
                  {p.requested_at ? new Date(p.requested_at).toLocaleDateString() : ''}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="panel-card__muted">{t('stats.noPayouts')}</div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="stat-card">
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value">{value}</div>
    </div>
  );
}

// A tiny inline bar chart (no chart lib needed).
function MiniChart({ data }) {
  const max = Math.max(...data.map((d) => Number(d.revenue) || 0), 1);
  return (
    <div className="mini-chart">
      {data.map((d, i) => (
        <div
          key={i}
          className="mini-chart__bar"
          style={{ height: `${Math.max(4, (Number(d.revenue) / max) * 100)}%` }}
          title={`${d.revenue}`}
        />
      ))}
    </div>
  );
}

// ── Members tab ─────────────────────────────────────────────────────────────

// `readOnly` is the embedded case: adding a member needs an Outfino account to
// attach to, and the embedded app has no Outfino identity to search users with.
function MembersTab({ brand, currentUserId, isAdmin, readOnly = false }) {
  const { t } = useLocale();
  const [members, setMembers] = useState(null);
  const [busy, setBusy] = useState(null);
  const [adding, setAdding] = useState(false);
  // The member waiting for a yes in the remove dialog: { member, display }.
  const [confirmRemove, setConfirmRemove] = useState(null);
  const { embedded, user } = useAuth();
  const devLocked = brand.environment === 'dev' || user?.environment === 'dev';
  const canAdd = !readOnly && !devLocked;

  function load() {
    fetchBrandMembers(brand.id)
      .then((list) => setMembers(Array.isArray(list) ? list : list?.data ?? []))
      .catch(() => setMembers([]));
  }
  useEffect(load, [brand.id]);

  // Ids already in the brand, so search results can hide existing members.
  const existingIds = useMemo(
    () =>
      new Set(
        (members || []).map((m) => m.user?.id || m.userId || m.user_id)
      ),
    [members]
  );

  const canEditPerms =
    !readOnly &&
    (embedded ||
      isAdmin ||
      (members || []).some(
        (m) => (m.user?.id || m.userId || m.user_id) === currentUserId && m.role === 'owner'
      ));

  function permsOf(m) {
    const p = m.permissions;
    if (Array.isArray(p)) return p;
    if (p && typeof p === 'object') return Object.keys(p).filter((k) => p[k]);
    return [];
  }

  async function togglePerm(m, key) {
    const uid = m.user?.id || m.userId || m.user_id;
    const cur = new Set(permsOf(m));
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    const next = [...cur];
    setBusy(uid);
    try {
      await updateBrandMember(brand.id, uid, next, 'member');
      load();
    } catch {
      /* ignore */
    } finally {
      setBusy(null);
    }
  }

  async function remove(m) {
    const uid = m.user?.id || m.userId || m.user_id;
    setBusy(uid);
    try {
      await removeBrandMember(brand.id, uid);
      load();
    } catch {
      /* ignore */
    } finally {
      setBusy(null);
    }
  }

  async function handleAdd(u) {
    // New members start with view-only access; the owner grants more via chips.
    const uid = u.id;
    try {
      await addBrandMember(brand.id, uid, ['viewProducts'], 'member');
      setAdding(false);
      load();
    } catch {
      /* ignore — likely already a member */
    }
  }

  return (
    <div className="panel-card">
      <div className="panel-card__row">
        <h2 className="panel-card__title">{t('members.title')}</h2>
        {canAdd && (
          <button
            className="btn-o btn-o--dark"
            onClick={() => setAdding((v) => !v)}
          >
            <PersonPlus size={15} style={{ marginRight: 6 }} />
            {t('members.add')}
          </button>
        )}
      </div>

      {devLocked && !readOnly && (
        <div className="panel-card__muted">{t('members.devLocked')}</div>
      )}

      {canAdd && adding && (
        <AddMemberBox
          existingIds={existingIds}
          onPick={handleAdd}
          onClose={() => setAdding(false)}
        />
      )}

      {members === null ? (
        <div className="panel-card__muted">{t('shell.loading')}</div>
      ) : members.length === 0 ? (
        <div className="panel-card__muted">{t('members.empty')}</div>
      ) : (
        <div className="member-list">
          {members.map((m) => {
            const u = m.user || m;
            const uid = u.id || m.userId || m.user_id;
            const isOwner = m.role === 'owner';
            const mine = permsOf(m);
            // Mirror the mobile app's User.displayUsername: show `@username`,
            // or the localized "Névtelen"/"Anonymous" fallback — never first_name.
            const username = u.username || u.userName || u.handle || null;
            const display = username ? `@${username}` : t('members.anonymous');
            const initial = (username || '?')[0].toUpperCase();
            return (
              <div className="member-row" key={uid}>
                <div className="member-row__id">
                  {u.avatarUrl ? (
                    <img className="member-row__avatar" src={u.avatarUrl} alt="" />
                  ) : (
                    <span className="member-row__avatar member-row__avatar--ph">
                      {initial}
                    </span>
                  )}
                  <div>
                    <div className="member-row__name">
                      {display}
                      <span className={`member-row__owner${isOwner ? '' : ' member-row__owner--member'}`}>
                        {t(isOwner ? 'members.role.owner' : 'members.role.member')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="member-row__perms">
                  {MEMBER_PERMISSIONS.map((p) =>
                    isOwner ? (
                      <span key={p} className="perm-chip is-on is-locked">
                        {t(`perm.${p}`)}
                      </span>
                    ) : (
                      <button
                        key={p}
                        className={`perm-chip ${mine.includes(p) ? 'is-on' : ''}`}
                        disabled={!canEditPerms || busy === uid}
                        onClick={() => (canEditPerms ? togglePerm(m, p) : undefined)}
                      >
                        {t(`perm.${p}`)}
                      </button>
                    )
                  )}
                </div>

                {(!isOwner || isAdmin) && uid !== currentUserId && (
                  <button
                    className="member-row__remove"
                    disabled={readOnly || busy === uid}
                    onClick={() => (readOnly ? undefined : setConfirmRemove({ member: m, display }))}
                    title={t('members.remove')}
                  >
                    <Trash3 size={15} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <ConfirmDialog
        open={!!confirmRemove}
        title={t('members.removeConfirmTitle')}
        body={confirmRemove ? t('members.removeConfirmBody', { name: confirmRemove.display }) : ''}
        cancelLabel={t('confirm.back')}
        confirmLabel={t('members.remove')}
        onCancel={() => setConfirmRemove(null)}
        onConfirm={() => {
          const target = confirmRemove?.member;
          setConfirmRemove(null);
          if (target) remove(target);
        }}
      />
    </div>
  );
}

// ── Add-member search box ───────────────────────────────────────────────────

function AddMemberBox({ existingIds, onPick, onClose }) {
  const { t } = useLocale();
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  // Debounced username search (mirrors the mobile add-member flow).
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = setTimeout(() => {
      let alive = true;
      searchUsers(term)
        .then((rows) => {
          if (!alive) return;
          setResults(rows.filter((r) => !existingIds.has(r.id)));
        })
        .catch(() => alive && setResults([]))
        .finally(() => alive && setLoading(false));
      return () => {
        alive = false;
      };
    }, 300);
    return () => clearTimeout(id);
  }, [q, existingIds]);

  return (
    <div className="add-member">
      <div className="add-member__search">
        <Search size={15} className="add-member__search-icon" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('members.searchPlaceholder')}
        />
        <button className="add-member__close" onClick={onClose} title={t('shopify.cancel')}>
          <XLg size={15} />
        </button>
      </div>

      {q.trim().length >= 2 && (
        <div className="add-member__results">
          {loading ? (
            <div className="add-member__muted">{t('shell.loading')}</div>
          ) : !results || results.length === 0 ? (
            <div className="add-member__muted">{t('members.searchEmpty')}</div>
          ) : (
            results.map((u) => {
              const username = u.username || u.userName || null;
              const display = username ? `@${username}` : t('members.anonymous');
              const initial = (username || '?')[0].toUpperCase();
              return (
                <button
                  key={u.id}
                  className="add-member__result"
                  onClick={() => onPick(u)}
                >
                  {u.avatarUrl ? (
                    <img className="add-member__avatar" src={u.avatarUrl} alt="" />
                  ) : (
                    <span className="add-member__avatar add-member__avatar--ph">
                      {initial}
                    </span>
                  )}
                  <span className="add-member__result-name">{display}</span>
                  <PersonPlus size={16} className="add-member__result-add" />
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ── Small helpers ───────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}
